import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Flashlight, Loader, CameraOff } from 'lucide-react';
import { getBarcodeDetector, cameraErrorMessage } from '../../utils/barcodeDetector';
import './CameraScanner.css';

// Same code seen again within this long (continuous mode) is the same item still in view
const REPEAT_MS = 1500;
const DETECT_EVERY_MS = 120;

// Full-screen rear-camera barcode reader (iPhone, iPad, Android).
//
//   <CameraScanner open onDetected={(code) => ...} onClose={() => ...} continuous={false}
//                  feedback={{ status, title, detail }} />
//
// `feedback` (continuous mode) is the page's verdict on the last scan - e.g. "Not on this
// order" when picking - shown on the camera view, since the overlay covers the page.
// Single mode closes after the first code. Continuous mode (free-scan counting) stays open
// and reports each code, ignoring the same code while it stays in view.
const CameraScanner = ({ open, onDetected, onClose, continuous = false, feedback = null }) => {
  const videoRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [status, setStatus] = useState('starting'); // starting | scanning | error
  const [message, setMessage] = useState('');
  const [lastCode, setLastCode] = useState(null);
  const [torch, setTorch] = useState({ available: false, on: false });
  const trackRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let stopped = false;
    let timer = null;
    let stream = null;
    let busy = false;
    let last = { code: null, at: 0 };
    setStatus('starting');
    setMessage('');
    setLastCode(null);
    setTorch({ available: false, on: false });

    const stop = () => {
      stopped = true;
      clearInterval(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
      trackRef.current = null;
    };

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        if (stopped) { stop(); return; }
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();
        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        const caps = track && typeof track.getCapabilities === 'function' ? track.getCapabilities() : {};
        if (caps && caps.torch) setTorch({ available: true, on: false });

        const detector = await getBarcodeDetector();
        if (stopped) return;
        setStatus('scanning');
        timer = setInterval(async () => {
          if (busy || stopped || video.readyState < 2) return;
          busy = true;
          try {
            const found = await detector.detect(video);
            const code = found && found[0] && String(found[0].rawValue || '').trim();
            if (code && !stopped) {
              const now = Date.now();
              if (code === last.code && now - last.at < REPEAT_MS) {
                last = { code, at: now };
              } else {
                last = { code, at: now };
                if (!continuous) {
                  stop();
                  onDetectedRef.current(code);
                  onCloseRef.current();
                } else {
                  setLastCode({ code, at: now });
                  onDetectedRef.current(code);
                }
              }
            }
          } catch {
            // a frame that can't be read; try the next one
          } finally {
            busy = false;
          }
        }, DETECT_EVERY_MS);
      } catch (error) {
        console.error('Camera scan failed:', error);
        if (stopped) return;
        setStatus('error');
        setMessage(cameraErrorMessage(error));
        stop();
      }
    })();

    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      stop();
    };
  }, [open, continuous]);

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch.on }] });
      setTorch((t) => ({ ...t, on: !t.on }));
    } catch {
      setTorch({ available: false, on: false });
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="cam-overlay" role="dialog" aria-modal="true" aria-label="Scan with camera">
      <video ref={videoRef} className="cam-video" playsInline muted autoPlay />
      <div className="cam-frame" aria-hidden="true" />
      <div className="cam-top">
        <span className="cam-title">{continuous ? 'Point at each barcode' : 'Point the camera at the barcode'}</span>
        <button type="button" className="cam-btn" onClick={() => onCloseRef.current()} aria-label="Close camera">
          <X size={28} aria-hidden="true" />
        </button>
      </div>
      <div className="cam-bottom" aria-live="polite">
        {status === 'starting' && <p className="cam-msg"><Loader className="cam-spin" size={20} aria-hidden="true" /> Starting camera…</p>}
        {status === 'scanning' && !lastCode && <p className="cam-msg">Hold steady about 10–20 cm away</p>}
        {status === 'scanning' && lastCode && !feedback && <p className="cam-msg cam-msg-ok" key={lastCode.at}>Counted {lastCode.code}</p>}
        {status === 'scanning' && lastCode && feedback && (
          <p className={`cam-msg cam-msg-${feedback.status === 'success' ? 'ok' : feedback.status === 'warning' ? 'warn' : feedback.status === 'error' ? 'error' : 'info'}`} key={feedback.at || lastCode.at}>
            <span><strong>{feedback.title}</strong>{feedback.detail ? <><br />{feedback.detail}</> : null}</span>
          </p>
        )}
        {status === 'scanning' && continuous && lastCode && (
          <p className="cam-hint">Same item again? Move it out of view, then back.</p>
        )}
        {status === 'error' && <p className="cam-msg cam-msg-error"><CameraOff size={20} aria-hidden="true" /> {message}</p>}
        <div className="cam-actions">
          {torch.available && (
            <button type="button" className={`cam-btn cam-btn-wide ${torch.on ? 'is-on' : ''}`} onClick={toggleTorch} aria-pressed={torch.on}>
              <Flashlight size={22} aria-hidden="true" /> Light
            </button>
          )}
          <button type="button" className="cam-btn cam-btn-wide" onClick={() => onCloseRef.current()}>
            {continuous ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CameraScanner;
